/**
 * Checkin Page
 * 
 * Real-time event check-in interface for coaches and admins
 * Matches legacy ridercheckin.html functionality
 * - Auto-loads current/next practice
 * - Shows RSVP status and counts
 * - Filters by Group, RSVP, and Role
 * - Real-time updates via WebSocket with HTTP fallback
 * - Checkboxes for check-in/check-out
 * 
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 3.8
 */

import { useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { restClient, Event as ApiEvent, Rider, Group, EventType } from '../api/rest-client';
import { setRiderPresence, presenceMapFromBooleanState } from '../utils/attendanceEdit';
import { websocketClient, StatusUpdateMessage } from '../api/websocket-client';
import { useAuth } from '../auth/AuthProvider';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Button } from '../components/common/Button';
import { formatRiderName } from '../utils/riderDisplay';
import { parseExpectedAttendance, getRSVPStatus as getSharedRSVPStatus, RSVPDisplayStatus } from '../utils/rsvpUtils';
import { filterRidersByEvent } from '../utils/eventFiltering';
import { resolveEffectiveEventId } from '../utils/checkinNav';
import { useGroupLeaderStore } from '../store/groupLeaderStore';
import { SkillLevel, SKILL_LEVEL_OPTIONS, riderMatchesSkillLevel } from '../utils/skillLevelFilter';

interface RiderWithRole extends Rider {
  role: string;
}

export default function Checkin() {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const { user } = useAuth();
  const isAdmin = user?.groups?.includes('admin') ?? false;
  const isGroupLeader = useGroupLeaderStore(s => s.isGroupLeader);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [practice, setPractice] = useState<ApiEvent | null>(null);
  // Past-edit mode: an admin opened a specific historical event to correct its
  // check-in/check-out. In this mode there is no live session — persistence is
  // via HTTP (PUT /events) and the WebSocket is not opened.
  const [pastEditMode, setPastEditMode] = useState(false);
  const [riders, setRiders] = useState<RiderWithRole[]>([]);
  const [allRiders, setAllRiders] = useState<RiderWithRole[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [connected, setConnected] = useState(false);
  const [httpFallback, setHttpFallback] = useState(false);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  
  // Filters
  const [groupFilter, setGroupFilter] = useState('');
  const [rsvpFilter, setRsvpFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [skillLevelFilter, setSkillLevelFilter] = useState<SkillLevel>('');
  
  // Checkin state
  const [checkinState, setCheckinState] = useState<Record<string, boolean>>({});
  const [checkoutState, setCheckoutState] = useState<Record<string, boolean>>({});
  const [updatingState, setUpdatingState] = useState<Record<string, boolean>>({});
  
  // Update queue to prevent duplicate updates
  const updateQueue = useRef<Map<string, number>>(new Map());

  /**
   * Load practice and riders on mount (and when admin/eventId resolve)
   */
  useEffect(() => {
    loadPractice();
    fetchEventTypes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, eventId]);

  /**
   * Fetch event types for color mapping
   */
  const fetchEventTypes = useCallback(async () => {
    try {
      const types = await restClient.getEventTypes();
      setEventTypes(types);
    } catch (error) {
      // Silent error handling - will use default colors
    }
  }, []);

  /**
   * Get color for event type badge
   */
  const getEventTypeColor = useCallback((eventTypeName: string): string => {
    const eventType = eventTypes.find(et => et.name === eventTypeName);
    return eventType?.color || '#435d7d'; // Default to primary blue
  }, [eventTypes]);

  /**
   * Setup WebSocket handlers
   */
  useEffect(() => {
    if (!practice || !user || pastEditMode) return;

    const unsubscribeStatus = websocketClient.onStatusUpdate(handleStatusUpdate);
    const unsubscribeConnection = websocketClient.onConnectionChange(handleConnectionChange);
    const unsubscribeError = websocketClient.onError(handleError);

    // Connect to WebSocket
    websocketClient.connect(practice.event_id, user.username || user.email || '');

    return () => {
      unsubscribeStatus();
      unsubscribeConnection();
      unsubscribeError();
      websocketClient.disconnect();
    };
  }, [practice, user, pastEditMode]);

  /**
   * Apply filters when they change
   */
  useEffect(() => {
    if (allRiders.length > 0 && practice) {
      applyFilters();
    }
  }, [groupFilter, rsvpFilter, roleFilter, skillLevelFilter, groups, allRiders, practice]);

  /**
   * Load current/next practice using aggregated endpoint
   * Phase 2 optimization: Single API call instead of 3
   */
  const loadPractice = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Only admins may open a specific event by id (past-event edit path).
      // Coaches always fall back to the backend's current/next auto-select, so
      // the Check-in screen shows the current/upcoming event and a coach never
      // requests a historical event (which the backend rejects with 403).
      const effectiveEventId = resolveEffectiveEventId(eventId, isAdmin);

      // Use new aggregated endpoint (Phase 2 optimization)
      const checkinData = await restClient.getCheckinData(effectiveEventId);
      
      if (!checkinData.current_practice) {
        setError('No practice found');
        setLoading(false);
        return;
      }

      setPractice(checkinData.current_practice);

      // Detect a historical event opened by an admin → past-edit mode.
      const endedMs = new Date(checkinData.current_practice.event_endtime).getTime();
      const historical = Number.isFinite(endedMs) && Date.now() > endedMs + 3 * 60 * 60 * 1000;
      setPastEditMode(Boolean(effectiveEventId) && historical);
      
      // Initialize checkin/checkout state from practice data
      initializeCheckinState(checkinData.current_practice);
      
      // Get current season (year)
      const currentSeason = new Date().getFullYear().toString();

      // Filter active riders from current season only
      const activeRiders = checkinData.riders.filter(r => 
        r.disabled !== '1' && r.disabled !== 1 && r.season === currentSeason
      );

      // Add role information
      const ridersWithRole: RiderWithRole[] = activeRiders.map(r => ({
        ...r,
        role: r.type || 'Student'
      }));

      // Filter by included_rider_ids when present (Requirements 16.1, 16.2)
      const invitedRiders = filterRidersByEvent(ridersWithRole, checkinData.current_practice);

      setAllRiders(invitedRiders);
      setGroups(checkinData.groups);
      
    } catch (err) {
      setError('Failed to load practice');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, eventId]);

  /**
   * Initialize checkin/checkout state from practice data
   */
  const initializeCheckinState = useCallback((practiceData: ApiEvent) => {
    try {
      const checkinData = practiceData.event_checkin 
        ? (typeof practiceData.event_checkin === 'string' 
            ? JSON.parse(practiceData.event_checkin) 
            : practiceData.event_checkin)
        : {};
      
      const checkoutData = practiceData.event_checkout
        ? (typeof practiceData.event_checkout === 'string'
            ? JSON.parse(practiceData.event_checkout)
            : practiceData.event_checkout)
        : {};

      // Convert to boolean state
      const checkinBoolState: Record<string, boolean> = {};
      const checkoutBoolState: Record<string, boolean> = {};

      Object.keys(checkinData).forEach(riderId => {
        if (checkinData[riderId] === 1) {
          checkinBoolState[riderId] = true;
        }
      });

      Object.keys(checkoutData).forEach(riderId => {
        if (checkoutData[riderId] === 1) {
          checkoutBoolState[riderId] = true;
        }
      });

      setCheckinState(checkinBoolState);
      setCheckoutState(checkoutBoolState);
    } catch (error) {
      // Error initializing checkin state
    }
  }, []);

  /**
  /**
   * Apply filters to riders list
   */
  const applyFilters = useCallback(() => {
    if (!practice) return;

    let filtered = [...allRiders];

    // Group filter
    if (groupFilter) {
      filtered = filtered.filter(r => r.group === groupFilter);
    }

    // RSVP filter
    if (rsvpFilter) {
      filtered = filtered.filter(r => getRSVPStatus(r.rider_id) === rsvpFilter);
    }

    // Role filter
    if (roleFilter) {
      if (roleFilter === 'coach') {
        filtered = filtered.filter(r => r.role.toLowerCase().includes('coach'));
      } else if (roleFilter === 'student') {
        filtered = filtered.filter(r => !r.role.toLowerCase().includes('coach'));
      }
    }

    // Skill Level filter (derived from the rider's group's group_skill_level)
    if (skillLevelFilter) {
      filtered = filtered.filter(r => riderMatchesSkillLevel(r, groups, skillLevelFilter));
    }

    // Sort: students first, then by name
    filtered.sort((a, b) => {
      const roleA = (a.role || 'student').toLowerCase();
      const roleB = (b.role || 'student').toLowerCase();
      
      if (roleA.includes('student') && !roleB.includes('student')) return -1;
      if (!roleA.includes('student') && roleB.includes('student')) return 1;
      
      return a.name.localeCompare(b.name);
    });

    setRiders(filtered);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practice, allRiders, groups, groupFilter, rsvpFilter, roleFilter, skillLevelFilter]);

  /**
   * Get RSVP status for a rider using shared utility
   */
  const getRSVPStatus = useCallback((riderId: string): RSVPDisplayStatus => {
    if (!practice || !practice.event_expectedAttendance) return 'no-response';

    const attendance = parseExpectedAttendance(practice.event_expectedAttendance);
    return getSharedRSVPStatus(attendance, riderId);
  }, [practice]);

  /**
   * Past-edit persistence (admin editing a historical event): save the updated
   * presence map via HTTP (PUT /events) instead of the live WebSocket.
   */
  const persistPastCheckin = useCallback(async (riderId: string, checked: boolean) => {
    if (!practice) return;
    const eid = practice.event_id;
    const queueKey = `${riderId}_checkin`;
    setUpdatingState(prev => ({ ...prev, [queueKey]: true }));
    // Optimistic update. For a past event, check-in and check-out move together
    // by default: checking a rider in also checks them out; unchecking clears both.
    setCheckinState(prev => ({ ...prev, [riderId]: checked }));
    setCheckoutState(prev => ({ ...prev, [riderId]: checked }));
    try {
      const nextCheckin = setRiderPresence(presenceMapFromBooleanState(checkinState), riderId, checked);
      await restClient.updateEvent(eid, { event_id: eid, event_checkin: JSON.stringify(nextCheckin) });
      // Mirror check-out to match check-in (ticked when checked in, cleared when not).
      const nextCheckout = setRiderPresence(presenceMapFromBooleanState(checkoutState), riderId, checked);
      await restClient.updateEvent(eid, { event_id: eid, event_checkout: JSON.stringify(nextCheckout) });
    } catch (e) {
      setCheckinState(prev => ({ ...prev, [riderId]: !checked }));
      setCheckoutState(prev => ({ ...prev, [riderId]: !checked }));
      setError('Failed to save check-in change');
    } finally {
      setUpdatingState(prev => { const n = { ...prev }; delete n[queueKey]; return n; });
    }
  }, [practice, checkinState, checkoutState]);

  const persistPastCheckout = useCallback(async (riderId: string, checked: boolean) => {
    if (!practice) return;
    const eid = practice.event_id;
    const queueKey = `${riderId}_checkout`;
    setUpdatingState(prev => ({ ...prev, [queueKey]: true }));
    setCheckoutState(prev => ({ ...prev, [riderId]: checked }));
    try {
      const nextCheckout = setRiderPresence(presenceMapFromBooleanState(checkoutState), riderId, checked);
      await restClient.updateEvent(eid, { event_id: eid, event_checkout: JSON.stringify(nextCheckout) });
    } catch (e) {
      setCheckoutState(prev => ({ ...prev, [riderId]: !checked }));
      setError('Failed to save check-out change');
    } finally {
      setUpdatingState(prev => { const n = { ...prev }; delete n[queueKey]; return n; });
    }
  }, [practice, checkoutState]);

  /**
   * Handle checkin checkbox change
   */
  const handleCheckinChange = useCallback((riderId: string, checked: boolean) => {
    if (pastEditMode) {
      void persistPastCheckin(riderId, checked);
      return;
    }
    const queueKey = `${riderId}_checkin`;
    
    // Prevent duplicate updates
    if (updateQueue.current.has(queueKey)) {
      return;
    }

    updateQueue.current.set(queueKey, Date.now());
    setUpdatingState(prev => ({ ...prev, [queueKey]: true }));

    // Optimistic update
    setCheckinState(prev => ({ ...prev, [riderId]: checked }));
    
    // If unchecking, also uncheck checkout
    if (!checked) {
      setCheckoutState(prev => ({ ...prev, [riderId]: false }));
    }

    // Send update
    websocketClient.updateStatus(riderId, 'checkin', checked, `${queueKey}_${Date.now()}`);

    // Clear updating state after timeout
    setTimeout(() => {
      updateQueue.current.delete(queueKey);
      setUpdatingState(prev => {
        const newState = { ...prev };
        delete newState[queueKey];
        return newState;
      });
    }, 5000);
  }, [pastEditMode, persistPastCheckin]);

  /**
   * Handle checkout checkbox change
   */
  const handleCheckoutChange = useCallback((riderId: string, checked: boolean) => {
    if (pastEditMode) {
      void persistPastCheckout(riderId, checked);
      return;
    }
    const queueKey = `${riderId}_checkout`;
    
    // Prevent duplicate updates
    if (updateQueue.current.has(queueKey)) {
      return;
    }

    updateQueue.current.set(queueKey, Date.now());
    setUpdatingState(prev => ({ ...prev, [queueKey]: true }));

    // Optimistic update
    setCheckoutState(prev => ({ ...prev, [riderId]: checked }));

    // Send update
    websocketClient.updateStatus(riderId, 'checkout', checked, `${queueKey}_${Date.now()}`);

    // Clear updating state after timeout
    setTimeout(() => {
      updateQueue.current.delete(queueKey);
      setUpdatingState(prev => {
        const newState = { ...prev };
        delete newState[queueKey];
        return newState;
      });
    }, 5000);
  }, [pastEditMode, persistPastCheckout]);

  /**
   * Handle WebSocket status update
   */
  const handleStatusUpdate = useCallback((message: StatusUpdateMessage) => {
    const queueKey = `${message.rider_id}_${message.action}`;
    updateQueue.current.delete(queueKey);
    
    setUpdatingState(prev => {
      const newState = { ...prev };
      delete newState[queueKey];
      return newState;
    });

    if (message.action === 'checkin') {
      setCheckinState(prev => ({ ...prev, [message.rider_id]: message.status }));
      
      // If unchecked, also uncheck checkout
      if (!message.status) {
        setCheckoutState(prev => ({ ...prev, [message.rider_id]: false }));
      }
    } else if (message.action === 'checkout') {
      setCheckoutState(prev => ({ ...prev, [message.rider_id]: message.status }));
    }
  }, []);

  /**
   * Handle WebSocket connection change
   */
  const handleConnectionChange = useCallback((isConnected: boolean, isHttpFallback: boolean) => {
    setConnected(isConnected);
    setHttpFallback(isHttpFallback);
  }, []);

  /**
   * Handle WebSocket error
   */
  const handleError = useCallback((_errorMessage: string) => {
    // WebSocket error occurred
  }, []);

  /**
   * Get unique groups for filter, sorted by order
   */
  const uniqueGroupNames = [...new Set(allRiders.map(r => r.group).filter(g => g && g !== 'N/A'))];
  const sortedGroups = uniqueGroupNames
    .map(groupName => {
      const groupData = groups.find(g => g.name === groupName);
      return {
        name: groupName,
        order: groupData?.order || 999
      };
    })
    .sort((a, b) => a.order - b.order)
    .map(g => g.name);

  /**
   * Calculate RSVP counts
   */
  const getRSVPCounts = () => {
    const coaches = allRiders.filter(r => r.role.toLowerCase().includes('coach'));
    const students = allRiders.filter(r => !r.role.toLowerCase().includes('coach'));

    const countRSVPs = (riderList: RiderWithRole[]) => {
      const yes = riderList.filter(r => getRSVPStatus(r.rider_id) === 'coming').length;
      const maybe = riderList.filter(r => getRSVPStatus(r.rider_id) === 'maybe').length;
      const no = riderList.filter(r => getRSVPStatus(r.rider_id) === 'not-coming').length;
      const none = riderList.filter(r => getRSVPStatus(r.rider_id) === 'no-response').length;
      return { yes, maybe, no, none };
    };

    const coachCounts = countRSVPs(coaches);
    const studentCounts = countRSVPs(students);

    return {
      coaches: coachCounts,
      students: studentCounts,
      total: {
        yes: coachCounts.yes + studentCounts.yes,
        maybe: coachCounts.maybe + studentCounts.maybe,
        no: coachCounts.no + studentCounts.no,
        none: coachCounts.none + studentCounts.none
      }
    };
  };

  /**
   * Check if checkin is available
   */
  const isCheckinAvailable = () => {
    if (!practice || !user) return false;
    
    const isCanceled = practice.event_status === 'canceled';
    if (isCanceled) return false;

    // Admins can always checkin
    if (user.groups?.includes('admin')) return true;

    // For coaches, check time restrictions
    const now = new Date();
    const practiceStart = new Date(practice.event_starttime);
    const practiceEnd = new Date(practice.event_endtime);
    const oneHourBeforeStart = new Date(practiceStart.getTime() - (60 * 60 * 1000));
    const threeHoursAfterEnd = new Date(practiceEnd.getTime() + (3 * 60 * 60 * 1000));

    return now >= oneHourBeforeStart && now <= threeHoursAfterEnd;
  };

  /**
   * Check if checkout is available
   */
  const isCheckoutAvailable = () => {
    if (!practice) return false;
    return practice.event_status !== 'canceled';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !practice) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error || 'No practice found'}
        </div>
        <Button variant="secondary" onClick={() => navigate('/')}>
          Back to Home
        </Button>
      </div>
    );
  }

  const rsvpCounts = getRSVPCounts();
  const isCanceled = practice.event_status === 'canceled';
  const checkinAvailable = isCheckinAvailable();
  const checkoutAvailable = isCheckoutAvailable();

  return (
    <div className="min-h-screen bg-primary-50 pb-8">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <h1 className={`text-3xl font-bold text-gray-900 ${isCanceled ? 'line-through' : ''}`}>
                {practice.event_name}
              </h1>
              {isCanceled && (
                <span className="inline-block px-3 py-1 text-base font-semibold text-white bg-red-500 rounded-full">
                  Canceled
                </span>
              )}
            </div>
            {/* Connection Status (hidden when editing a past event — no live session) */}
            {!pastEditMode && (
              <span className={`px-3 py-1 rounded text-sm font-medium ${
                connected 
                  ? 'bg-green-100 text-green-800'
                  : httpFallback
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {connected ? 'Connected' : httpFallback ? 'HTTP Mode' : 'Disconnected'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Past-event edit banner (admin correcting a historical event) */}
      {pastEditMode && (
        <div
          data-testid="past-event-banner"
          className="bg-amber-50 border-b border-amber-200 text-amber-800 px-4 py-3"
        >
          <div className="container mx-auto">
            <span className="font-semibold">Editing past event:</span>{' '}
            {practice.event_name} —{' '}
            {new Date(practice.event_starttime).toLocaleDateString('en-US', {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            })}
            . Changes are saved immediately.
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="container mx-auto px-2 md:px-4 pt-3 pb-6">
        {/* Refresh Button */}
        <div className="mb-4">
          <Button onClick={loadPractice} className="w-full md:w-auto">
            <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh Practice
          </Button>
        </div>

        {/* Event Details with RSVP Summary */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <span 
              className="inline-block px-3 py-1 text-base font-semibold text-white rounded-full"
              style={{ backgroundColor: getEventTypeColor(practice.event_type) }}
            >
              {practice.event_type}
            </span>
            {isCanceled && (
              <span className="inline-block px-3 py-1 text-base font-semibold text-white bg-red-500 rounded-full">
                Canceled
              </span>
            )}
          </div>
          
          <h2 className={`text-xl font-semibold text-gray-900 mb-4 ${isCanceled ? 'line-through' : ''}`}>
            Event Details
          </h2>
          
          <div className="space-y-4 mb-6">
            {/* Date and Time */}
            <div className="flex items-start">
              <svg
                className="w-6 h-6 mr-3 text-gray-400 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <div>
                <p className={`text-base text-gray-900 ${isCanceled ? 'line-through' : ''}`}>
                  {new Date(practice.event_starttime).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                <p className={`text-base text-gray-600 ${isCanceled ? 'line-through' : ''}`}>
                  {new Date(practice.event_starttime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} to {new Date(practice.event_endtime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </p>
              </div>
            </div>

            {/* Location */}
            <div className="flex items-start">
              <svg
                className="w-6 h-6 mr-3 text-gray-400 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <div className="flex-1">
                <p className={`text-base text-gray-900 ${isCanceled ? 'line-through' : ''}`}>
                  {practice.event_location_name || practice.event_location || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* RSVP Summary */}
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">RSVP Summary</h3>
            
            {/* Summary Boxes */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="flex flex-col items-center p-3 bg-green-50 rounded-md">
                <span className="text-sm font-medium text-gray-700 mb-1">Yes</span>
                <span className="text-2xl font-bold text-green-600">{rsvpCounts.total.yes}</span>
              </div>
              <div className="flex flex-col items-center p-3 bg-orange-50 rounded-md">
                <span className="text-sm font-medium text-gray-700 mb-1">Maybe</span>
                <span className="text-2xl font-bold text-orange-500">{rsvpCounts.total.maybe}</span>
              </div>
              <div className="flex flex-col items-center p-3 bg-red-50 rounded-md">
                <span className="text-sm font-medium text-gray-700 mb-1">No</span>
                <span className="text-2xl font-bold text-red-600">{rsvpCounts.total.no}</span>
              </div>
              <div className="flex flex-col items-center p-3 bg-gray-50 rounded-md">
                <span className="text-sm font-medium text-gray-700 mb-1">No Response</span>
                <span className="text-2xl font-bold text-gray-900">{rsvpCounts.total.none}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">Filter by Group:</label>
              <select
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Groups</option>
                {sortedGroups.map(group => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">Filter by RSVP:</label>
              <select
                value={rsvpFilter}
                onChange={(e) => setRsvpFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All RSVP</option>
                <option value="coming">Yes</option>
                <option value="maybe">Maybe</option>
                <option value="not-coming">No</option>
                <option value="no-response">No Response</option>
              </select>
            </div>
            
            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">Filter by Role:</label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Roles</option>
                <option value="coach">Coaches</option>
                <option value="student">Students</option>
              </select>
            </div>

            <div>
              <label className="block text-base font-medium text-gray-700 mb-2">Filter by Skill Level:</label>
              <select
                data-testid="checkin-skill-level-filter"
                value={skillLevelFilter}
                onChange={(e) => setSkillLevelFilter(e.target.value as SkillLevel)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Skill Levels</option>
                {SKILL_LEVEL_OPTIONS.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Riders Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left px-2 md:px-4 py-3 text-base font-semibold text-gray-700 uppercase">Rider</th>
                  <th className="text-left px-2 md:px-4 py-3 text-base font-semibold text-gray-700 uppercase">RSVP</th>
                  <th className="text-center px-2 md:px-4 py-3 text-base font-semibold text-gray-700 uppercase">Check-in</th>
                  <th className="text-center px-2 md:px-4 py-3 text-base font-semibold text-gray-700 uppercase">Check-out</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {riders.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-gray-600">
                      No riders found
                    </td>
                  </tr>
                ) : (
                  riders.map(rider => {
                    const rsvpStatus = getRSVPStatus(rider.rider_id);
                    const isCheckedIn = checkinState[rider.rider_id] || false;
                    const isCheckedOut = checkoutState[rider.rider_id] || false;
                    const checkinUpdating = updatingState[`${rider.rider_id}_checkin`] || false;
                    const checkoutUpdating = updatingState[`${rider.rider_id}_checkout`] || false;

                    return (
                      <tr key={rider.rider_id} className="hover:bg-gray-50">
                        <td className="px-2 md:px-4 py-3">
                          <button
                            onClick={() => {
                              const isCoach = rider.role.toLowerCase().includes('coach');
                              if (isCoach) {
                                const levelText = rider.level ? `Coach Level ${rider.level}` : 'Coach Level MISSING';
                                alert(`Group: ${rider.group || 'N/A'}\n${levelText}`);
                              } else {
                                alert(`Group: ${rider.group || 'N/A'}\nRace Category: ${rider.category || 'N/A'}\nGrade: ${rider.grade || 'N/A'}`);
                              }
                            }}
                            className="text-primary-600 hover:text-primary-800 underline cursor-pointer text-left"
                          >
                            {formatRiderName(rider)}{isGroupLeader(rider.rider_id) ? ' ⭐' : ''}
                          </button>
                        </td>
                        <td className="px-2 md:px-4 py-3">
                          {rsvpStatus === 'coming' && <span className="text-green-600 font-medium">Yes</span>}
                          {rsvpStatus === 'maybe' && <span className="text-orange-500 font-medium">Maybe</span>}
                          {rsvpStatus === 'not-coming' && <span className="text-red-600 font-medium">No</span>}
                          {rsvpStatus === 'no-response' && <span className="text-gray-500">No Response</span>}
                        </td>
                        <td className="px-2 md:px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={isCheckedIn}
                            disabled={!checkinAvailable || checkinUpdating}
                            onChange={(e) => handleCheckinChange(rider.rider_id, e.target.checked)}
                            className="w-6 h-6 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </td>
                        <td className="px-2 md:px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={isCheckedOut}
                            disabled={!isCheckedIn || !checkoutAvailable || checkoutUpdating}
                            onChange={(e) => handleCheckoutChange(rider.rider_id, e.target.checked)}
                            className="w-6 h-6 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
